import {
  Flex,
  Stack, Text
} from "@mantine/core";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAccountContacts, useViews, useWorkspaceMetricPreferences, useWorkspacePreferences } from "../../hooks/QueryHooks";
import TrendsContainer from "../../pages/home/account-details/account-overview/components/TrendsContainer";
import KeyRole from "../../pages/home/account-details/components/KeyRoles";
import HqAccountTraits from "../../pages/home/headquarters/components/HqAccountTraits";
import HqUserTraits from "../../pages/home/headquarters/components/HqUserTraits";
import UserActivityTablsSm from "../../pages/home/headquarters/components/UserActivityTableSm";
import useWorkspaceStore from "../../store/workspace/workspaceStore";
import Pill from "../ui/badges/Pill";
import ProfileImg from "../ui/icons/ProfileImg";

type Props = {
  entity: any,
  workspaceId: any,
  closeDrawer: () => void,
  entityRelation?: "accounts" | "users",
};

const EntityDetails = ({ entity, workspaceId, closeDrawer, entityRelation = "accounts" }: Props) => {
  const params = useParams();
  const { data }: any = useViews({
    workspaceId,
  });

  const navigate = useNavigate();

  const currentWorkspace = useWorkspaceStore((state) => state.workspace);

  const prefs: any = useWorkspacePreferences({
    workspaceId,
    enabled: !!workspaceId,
  });

  const metrics: any = useWorkspaceMetricPreferences({
    workspaceId,
    enabled: !!workspaceId,
  });

  const contacts: any = useAccountContacts({
    workspaceId,
    accountId: entity?.external_id,
    enabled: entity?.external_id !== "" && entityRelation === "accounts",
  });

  const defaultView = useMemo(() => (
    data?.filter((view: any) => view?.is_precreated)?.reduce((ob: any, val: any) => {
      ob[val?.entity_relation] = val;
      return ob;
    }, {} as any)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [data?.length]);

  let path: string;
  if (entityRelation === "accounts") {
    path = `/home/${params?.workspaceId}/view/${defaultView?.accounts?.id}/account/${entity?.external_id}/overview`;
  } else {
    path = `/home/${params?.workspaceId}/view/${defaultView?.users?.id}/user/${entity?.external_id}`;
  }

  const tableHeight = useMemo(
    () => (window.innerHeight * 0.35),
    [],
  );

  return (
    <div className="w-full h-full py-5" key={entityRelation}>
      <Stack gap={20}>
        <Flex gap={15}>
          <ProfileImg img={entity?.avatar_url} name={entity?.name} size="lg" />
          <Stack gap={0.5} className="overflow-hidden group" justify="center">
            <Flex gap={10} justify="center" align="center">
              <Text truncate size="lg" fw={600} c="text.9">{entity?.name || "-"}</Text>
              {entityRelation === "accounts" && (
                <Pill
                  color="green"
                  rounded
                  size="md"
                  className=" min-w-[40px] items-center justify-center !font-semibold"
                  text={`$${entity?.mrr || 0} MRR`}
                />
              )}
            </Flex>
            <button
              className="text-xs text-purple-700 hover:text-purple-900 !p-0 !w-fit font-semibold "
              onClick={() => {
                navigate(path);
                closeDrawer();
              }}
              type="button"
            >
              View profile
            </button>
          </Stack>
        </Flex>
        {entityRelation === "accounts"
          ? (
            <>
              <HqAccountTraits
                account={entity}
                keyRolesComponent={
                  <KeyRole
                    key={`${entity?.external_id}`}
                    containerClass="w-full h-auto"
                    accountId={entity?.external_id}
                    contacts={contacts?.data?.items}
                    csm={entity?.csm_id}
                    ae={entity?.ae_id}
                    owner={entity?.account_champion_id}
                    collapsible={false}
                  />
                }
              />
              {prefs.isSuccess && metrics.isSuccess && entityRelation === "accounts" && (
                <TrendsContainer
                  prefs={prefs}
                  metrics={metrics?.data?.filter((m: any) => m.linked_entity === "accounts")}
                  account={entity}
                  currentWorkspace={currentWorkspace || { id: workspaceId }}
                  containerClassName="border !border-r rounded-md border-gray-200"
                />
              )}
            </>
          ) : (
            <>
              <HqUserTraits contact={entity} />
              <UserActivityTablsSm
                defaultOpen
                headingClass="text-xs py-1.5 pl-1 uppercase text-gray-500 font-semibold tracking-wider"
                tableHeight={tableHeight}
                userId={entity?.external_id}
              />
            </>
          )}
      </Stack>
    </div>
  );
};

export default EntityDetails;
